import React, { useState, useContext } from 'react';
import { 
  HelpCircle, 
  Book, 
  Users, 
  Search, 
  MessageSquare, 
  Settings, 
  User,
  ChevronDown,
  ChevronRight,
  Mail,
  Send,
  FileText,
  Upload,
  Star,
  Shield,
  AlertCircle
} from 'lucide-react';
import AuthContext from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const HelpPage = () => {
  const { user } = useContext(AuthContext);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState('getting-started');
  const [contactForm, setContactForm] = useState({
    subject: '',
    category: 'general',
    message: '',
    email: user?.email || ''
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...contactForm,
          userId: user?.id,
          userName: user?.name
        })
      });

      if (response.ok) {
        toast.success('Your message has been sent successfully!');
        setContactForm({
          subject: '',
          category: 'general',
          message: '',
          email: user?.email || ''
        });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const helpSections = {
    'getting-started': {
      title: 'Getting Started',
      icon: <HelpCircle className="w-5 h-5" />,
      content: [
        {
          id: 'first-login',
          question: 'What should I do after logging in for the first time?',
          answer: `After your first login:
          1. Complete your profile by uploading a profile picture to help others recognize you
          2. Browse available courses in your school
          3. Join course sections you're enrolled in
          4. Start exploring materials shared by other students`
        },
        {
          id: 'navigation',
          question: 'How do I navigate the platform?',
          answer: `Use the sidebar menu to access different sections:
          • Home: Overview of your school and recent activity
          • Courses: Browse all available courses
          • Professors: View professor information and ratings
          • My Courses: See courses you've joined
          • My Materials: Manage materials you've uploaded
          • Profile: Update your personal information`
        },
      ]
    },
    'courses-materials': {
      title: 'Courses & Materials',
      icon: <Book className="w-5 h-5" />,
      content: [
        {
          id: 'joining-courses',
          question: 'How do I join a course section?',
          answer: `To join a course section:
          1. Navigate to "Courses" from the sidebar
          2. Find your course using search or browsing
          3. Click on the course to view available sections
          4. Click "Join Section" on the section for your term/professor
          5. You'll now see updates from this section in "My Courses"`
        },
        {
          id: 'uploading-materials',
          question: 'How do I upload study materials?',
          answer: `To share materials:
          1. Go to the specific course section
          2. Click "Upload Material"
          3. Choose your file (PDF, DOCX, images, etc.)
          4. Select the material type (notes, exam, etc.)
          5. Add a descriptive title and optional description
          6. Click "Upload" to share with your peers`
        },
        {
          id: 'material-types',
          question: 'What types of materials can I upload?',
          answer: `We support student-generated educational materials in these categories:
          
          📝 **Study Notes** - Personal notes and summaries created by students
          📚 **Study Guides** - Student-created guides for exam preparation  
          💡 **Practice Materials** - Student-generated practice problems and exercises
          🔗 **Course Resources** - Educational resources and supplementary materials
          👥 **Group Study** - Collaborative study materials created by student groups
          
          **Supported file formats:** PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, PNG, JPG
          **Maximum file size:** 10MB per file`
        },
      ]
    },
    'questions-answers': {
      title: 'Questions & Answers',
      icon: <MessageSquare className="w-5 h-5" />,
      content: [
        {
          id: 'asking-questions',
          question: 'How do I ask a question about my course?',
          answer: `To ask a question:
          1. Navigate to the specific course
          2. Click on the "Questions" tab
          3. Click "Ask a Question"
          4. Write a clear, specific question
          5. Submit your question for others to answer`
        },
        {
          id: 'answering',
          question: 'How do I answer questions?',
          answer: `To help your peers:
          1. Browse questions in courses you're familiar with
          2. Click on a question to view it fully
          3. Type your answer in the answer box
          4. Use formatting tools to make your answer clear
          5. Submit your answer
          6. You can also reply to other answers for discussion
          7. Gain merit if your answers are upvoted or accepted`
        },
        {
          id: 'voting',
          question: 'What is the voting system?',
          answer: 'Both questions and answers can be upvoted or downvoted. This helps identify the most helpful content. Question askers can mark an answer as "Accepted" to indicate it solved their problem.'
        }
      ]
    },
    'collaboration': {
      title: 'Collaboration & Community',
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          id: 'comments',
          question: 'How do I comment on materials?',
          answer: 'Open any material and open the comments section. You can add comments to discuss the material, ask for clarifications, or provide additional context. Comments can be liked by other users.'
        },
        {
          id: 'merit-points',
          question: 'What are Merit Points?',
          answer: `Merit Points recognize your contributions to your school community and recognize:
          • Upvoted materials
          • Uvoted answers
          • Accepted answers
          Similarly, downvotes will subtract from your merit score.

          Merit Points showcase your helpfulness to the community!`
        },
        {
          id: 'profiles',
          question: 'How do public profiles work?',
          answer: 'Your public profile shows your contributions, merit points, and activity. Other students can view your profile to see materials you\'ve shared and questions you\'ve answered.'
        }
      ]
    },
    'account-settings': {
      title: 'Account & Settings',
      icon: <Settings className="w-5 h-5" />,
      content: [
        {
          id: 'profile-update',
          question: 'How do I update my profile?',
          answer: `Update your profile:
          1. Click "Profile" in the sidebar
          2. Click "Edit Profile"
          3. Update your information
          4. Upload or change profile picture
          5. Save your changes`
        },
        {
          id: 'theme',
          question: 'Can I change the appearance?',
          answer: 'Yes! Click the theme button in the header to switch between different color themes including dark mode, light mode, and various color schemes. Your preference is saved automatically.'
        },
      ]
    },
    'policies': {
      title: 'Policies & Guidelines',
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          id: 'academic-integrity',
          question: 'How does EDX Red ensure academic integrity?',
          answer: `EDX Red is committed to upholding the highest standards of academic integrity:
          
          🛡️ **Compliance Monitoring:**
          • All uploads are automatically scanned for prohibited content
          • AI-powered detection of instructor materials and copyrighted content
          • Manual review process for flagged materials
          • Real-time compliance checking during upload
          
          ✅ **What You CAN Share:**
          • Personal study notes and summaries
          • Student-created study guides and materials
          • Practice problems you wrote or found publicly
          • Educational resources and references
          • Collaborative notes from study groups
          
          ⚖️ **Enforcement:**
          • Non-compliant materials are automatically flagged for review
          • Repeated violations may result in account restrictions
          • Community reporting helps maintain standards
          • Regular policy updates based on university requirements
          
          **Remember:** When you upload, you're certifying that the content is student-created and complies with academic integrity policies.`
        },
        {
          id: 'content-guidelines',
          question: 'What content is not allowed?',
          answer: `To maintain academic integrity compliance, the following content is **strictly prohibited**:
          
          🚫 **Instructor Materials:**
          • Course syllabi or instructor-created documents
          • Professor's lecture slides or presentations
          • Faculty handouts or instruction sheets
          • Instructor manuals or guides
          
          🚫 **Copyrighted Content:**
          • Textbook pages or content (Pearson, McGraw-Hill, Cengage, etc.)
          • Publisher materials or resources
          • Copyrighted academic articles without permission
          
          🚫 **Academic Integrity Violations:**
          • Current exam questions or answer keys
          • Assignment solutions before due dates
          • Test banks or official exam materials
          • Homework solutions from current semesters
          
          🚫 **General Prohibited Content:**
          • Inappropriate or offensive material
          • Personal information of others
          • Spam or commercial content
          • Malicious files or viruses
          
          **Remember:** Only upload content that you created yourself or have explicit permission to share. When in doubt, don't upload it.`
        },
        {
          id: 'reporting',
          question: 'How do I report inappropriate content?',
          answer: 'Click the flag icon on any material, comment, or user profile to report it. Select the reason for reporting and provide details. Our moderation team will review reports promptly.'
        }
      ]
    }
  };

  const faqItems = [
    {
      question: 'Is EDX Red free to use?',
      answer: 'The Basic plan is completely free and includes everything except for AI functionality. The Pro plan, which includes AI features, costs $1.99/month.'
    },
    {
      question: 'Are my uploads anonymous?',
      answer: 'No, your name appears with your uploads to maintain accountability.'
    },
//  {
//      question: 'What happens to my data when I graduate?',
//      answer: 'Your account remains accessible as long as you have access to your university email.'
//    },
    {
      question: 'How are moderators chosen?',
      answer: 'Moderators are selected from active, trusted community members who demonstrate good judgment and helpfulness. Apply if you\'re interested!'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <HelpCircle className="w-8 h-8 text-red-600" />
          Help Center
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Find answers to common questions and learn how to make the most of EDX Red
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <nav className="sticky top-4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Help Topics</h3>
            <ul className="space-y-2">
              {Object.entries(helpSections).map(([key, section]) => (
                <li key={key}>
                  <button
                    onClick={() => setActiveTab(key)}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 transition-colors ${
                      activeTab === key
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {section.icon}
                    <span className="text-sm font-medium">{section.title}</span>
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => setActiveTab('faq')}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 transition-colors ${
                    activeTab === 'faq'
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">FAQ</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 transition-colors ${
                    activeTab === 'contact'
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-sm font-medium">Contact Support</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeTab !== 'faq' && activeTab !== 'contact' && helpSections[activeTab] && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                {helpSections[activeTab].icon}
                {helpSections[activeTab].title}
              </h2>
              <div className="space-y-4">
                {helpSections[activeTab].content.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleSection(item.id)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="font-medium text-gray-900 dark:text-white text-left">
                        {item.question}
                      </span>
                      {expandedSections[item.id] ? (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {expandedSections[item.id] && (
                      <div className="px-4 py-3 bg-white dark:bg-gray-800">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                          {item.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {activeTab === 'faq' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleSection(`faq-${index}`)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="font-medium text-gray-900 dark:text-white text-left">
                        {item.question}
                      </span>
                      {expandedSections[`faq-${index}`] ? (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {expandedSections[`faq-${index}`] && (
                      <div className="px-4 py-3 bg-white dark:bg-gray-800">
                        <p className="text-gray-700 dark:text-gray-300">
                          {item.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Form */}
          {activeTab === 'contact' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Mail className="w-6 h-6 text-red-600" />
                Contact Support
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Can't find what you're looking for? Send us a message and we'll get back to you as soon as possible.
              </p>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={contactForm.category}
                    onChange={(e) => setContactForm({ ...contactForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="general">General Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="content">Content Issue</option>
                    <option value="account">Account Problem</option>
                    <option value="suggestion">Feature Suggestion</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Please provide as much detail as possible..."
                    rows="6"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Note:</strong> For urgent issues, you can also email us directly at{' '}
                  <a href="mailto:contact@edx.red" className="underline">
                    contact@edx.red
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpPage;